import PageFilters, { Filter } from 'components/new/PageFilters';
import { CurrencyCode } from 'further-types/lp/vehicle';
import SearchField from 'further-ui/components/SearchField';
import Switch from 'further-ui/components/Switch';
import { useDisclosure, useFilters } from 'further-ui/hooks';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { Column } from 'further-ui/components/Layout';
import { Box, Typography, styled } from '@mui/material';
import { EmailOutlined, SaveAlt } from '@mui/icons-material';
import { differenceInDays } from 'date-fns';
import useCapitalActionNoticePdf from 'hooks/data/capitalActionNotice/useCapitalActionNoticePdf';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import api from 'lib/trpcClient';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import AvatarWithName from 'components/new/AvatarWithName';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

const ExpandedContentWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

type Props = {
  currencyLcy?: CurrencyCode;
  vehicleId: string;
  dataByInvestor: Array<{
    id: string;
    name: string;
    totalCallAmountLcy: number;
    amountPendingLcy: number;
    capitalActions: Array<{
      id: string;
      name: string;
      actionDueDate: CalendarDay;
      totalCallAmountLcy: number;
      amountPendingLcy: number;
      hasNotice: boolean;
    }>;
  }>;
};

const OutstandingCalls: React.FC<Props> = ({
  vehicleId,
  dataByInvestor,
  currencyLcy,
}) => {
  const handleTrpcMutation = useHandleTrpcMutation();
  const { downloadPdf, isDownloading } = useCapitalActionNoticePdf();
  const sendReminderDialog = useDisclosure();
  const [showSendReminder] = useHasPermissions([
    RolePermission.LpEditCapitalActionNotice,
  ]);
  const { mutateAsync: sendReminder, isPending: isSendingReminder } =
    api.capitalActionNotice.sendReminder.useMutation(
      handleTrpcMutation({
        successMessage: 'Reminder email sent successfully.',
        invalidations: [],
        onSuccess: () => {
          sendReminderDialog.onClose();
        },
      }),
    );
  const { toggleRowExpansion, expandedRows } = useExpandedRows();
  const { filters, handleChangeFilters } = useFilters<{
    investorName: string;
    showFullyPaid: boolean;
  }>(`capital-actions-outstanding-calls-${vehicleId}`, {
    defaultFilters: {
      investorName: '',
      showFullyPaid: false,
    },
  });

  const filteredData = dataByInvestor
    .filter((investor) => {
      const matchesName = investor.name
        .toLowerCase()
        .includes(filters.investorName.toLowerCase());
      const isFullyPaid = investor.amountPendingLcy === 0;

      if (filters.showFullyPaid) {
        return matchesName;
      }

      return matchesName && !isFullyPaid;
    })
    .map((investor) => ({
      ...investor,
      capitalActions: investor.capitalActions.filter((action) => {
        if (filters.showFullyPaid) {
          return true;
        }
        return action.amountPendingLcy > 0;
      }),
    }));

  const columns: Array<TableColumn<(typeof filteredData)[number]>> = [
    {
      key: 'investorName',
      label: 'Investor',
      render: ({ name }) => <AvatarWithName name={name} />,
    },
    {
      key: 'outstandingCalls',
      label: 'Outstanding calls',
      render: (row) =>
        row.capitalActions.filter((action) => action.amountPendingLcy > 0)
          .length,
    },
    {
      key: 'amountPendingLcy',
      label: 'Amount pending',
      render: (row) =>
        numberToCurrencyString(row.amountPendingLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: 'totalCallAmountLcy',
      label: 'Total call amount',
      render: (row) =>
        numberToCurrencyString(row.totalCallAmountLcy, {
          currency: currencyLcy,
        }),
    },
  ];

  return (
    <Column spacing="lg" fullWidth>
      <PageFilters justify="space-between">
        <Filter size="md">
          <SearchField
            clearable
            fullWidth
            placeholder="Search by investor"
            value={filters.investorName}
            autoSearchOnDebounce
            allowEmptyQuery
            onSearch={(query) => {
              handleChangeFilters({ investorName: query });
            }}
          />
        </Filter>
        <Switch
          label="Show fully paid investors"
          checked={filters.showFullyPaid}
          onChange={(checked) =>
            handleChangeFilters({ showFullyPaid: checked })
          }
        />
      </PageFilters>
      <Table
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        isExpandableRow={(row) => row.amountPendingLcy > 0}
        emptyMessage="No outstanding calls to show."
        getExpandedContent={(row) => {
          const capitalActions = row.capitalActions.map((action, index) => ({
            ...action,
            index,
          }));
          return (
            <ExpandedContentWrapper>
              <Table
                showHeaderBorder
                showTopBorder
                rowHasPendingAction={(action) =>
                  isDownloading(row.id, action.id)
                }
                actions={[
                  {
                    label: 'Download capital notice',
                    icon: SaveAlt,
                    hidden: (action) => !action.hasNotice,
                    onClick: ({ id: capitalActionId }) => {
                      downloadPdf({
                        investorId: row.id,
                        capitalActionId,
                        vehicleId,
                      });
                    },
                  },
                  {
                    label: 'Send reminder email',
                    icon: EmailOutlined,
                    tooltip: (action) =>
                      !action.hasNotice
                        ? "Reminder emails can't be sent until the capital notice has been published."
                        : undefined,
                    disabled: (action) =>
                      isSendingReminder || !action.hasNotice,
                    hidden: (action) =>
                      !showSendReminder || action.amountPendingLcy <= 0,
                    onClick: (capitalAction) =>
                      sendReminderDialog.stageAction({
                        capitalActionId: capitalAction.id,
                        investorId: row.id,
                      }),
                  },
                ]}
                columns={[
                  {
                    key: 'name',
                    label: 'Action name',
                    render: ({ name }) => (
                      <ColorIndicatorWithText text={name} colorKey={{ name }} />
                    ),
                  },
                  {
                    key: 'actionDueDate',
                    label: 'Due date',
                    render: ({ actionDueDate, amountPendingLcy }) => {
                      const today = new CalendarDay(new Date());
                      const daysUntilDue = differenceInDays(
                        actionDueDate.toUTCDate(),
                        today.toUTCDate(),
                      );
                      const daysUntilDueColor =
                        amountPendingLcy > 0 && daysUntilDue <= 0
                          ? 'error'
                          : 'grey.900';
                      return (
                        <Typography variant="h6" color={daysUntilDueColor}>
                          {actionDueDate.toReadable()}
                        </Typography>
                      );
                    },
                  },
                  {
                    key: 'totalCallAmountLcy',
                    label: 'Total call amount',
                    render: (action) =>
                      numberToCurrencyString(action.totalCallAmountLcy, {
                        currency: currencyLcy,
                      }),
                  },
                  {
                    key: 'amountPendingLcy',
                    label: 'Amount pending',
                    render: (action) =>
                      numberToCurrencyString(action.amountPendingLcy, {
                        currency: currencyLcy,
                      }),
                  },
                ]}
                rows={capitalActions}
              />
            </ExpandedContentWrapper>
          );
        }}
        isExpandedRow={(row) =>
          expandedRows.has(row.id) && row.amountPendingLcy > 0
        }
        columns={columns}
        rows={filteredData}
      />
      <ConfirmDialog
        open={sendReminderDialog.isOpen}
        loading={isSendingReminder}
        onClose={sendReminderDialog.onClose}
        actions={{
          confirm: {
            label: 'Send reminder',
            onClick: () => {
              sendReminder({
                investorId: sendReminderDialog.actionProps.investorId,
                capitalActionId: sendReminderDialog.actionProps.capitalActionId,
                vehicleId,
              });
            },
          },
          cancel: {
            label: 'Cancel',
            onClick: sendReminderDialog.onClose,
          },
        }}
        title="Send reminder email"
        content="This email will notify the investor that payment remains outstanding
            and will request payment."
      />
    </Column>
  );
};

export default OutstandingCalls;
